import * as React from 'react';

import BankBg from '@/assets/images/velodrome/img_bank_bg.svg';
import Skeleton from '@/components/elements/Skeleton';
import { VelodromeInfo } from '@/lib/microcms/model';
import { Velodrome } from '@/modelTypes';

import { Headline } from '../../style';
import {
  AccessField,
  AccessFields,
  AccessTableHeader,
  AnchorLink,
  BankDescription,
  BankDetail,
  BankImage,
  BankImageWrapper,
  BankInfo,
  BankTable,
  BankTableHeader,
  FeeTableHeader,
  Headline2,
  Section,
  Table,
  TableData,
  TableRow,
  TableWrapper,
  Text,
} from './style';

interface VelodromeInfoTemplateProps {
  velodromeName: Velodrome['name'];
  velodromeInfo: VelodromeInfo | undefined;
}

const VelodromeInfoTemplate: React.FC<VelodromeInfoTemplateProps> = ({
  velodromeName,
  velodromeInfo,
}) => {
  return (
    <>
      <Headline>{velodromeName}競輪場</Headline>
      <Section>
        <Headline2>バンク情報</Headline2>
        <BankInfo>
          <BankImageWrapper
            freq={velodromeInfo ? Number(velodromeInfo.direction[0]) : 0}
            perimeter={velodromeInfo ? Number(velodromeInfo.perimeter[0]) : 0}
          >
            <BankImage
              src={BankBg}
              alt={`周長${velodromeInfo ? velodromeInfo.perimeter : 0}のバンク`}
            />
          </BankImageWrapper>
          <BankDetail>
            <BankDescription>
              {velodromeInfo ? (
                velodromeInfo.description
              ) : (
                <Skeleton count={3} />
              )}
            </BankDescription>
            <TableWrapper>
              <BankTable>
                <tbody>
                  <TableRow>
                    <BankTableHeader>見なし直線距離</BankTableHeader>
                    <TableData>
                      {velodromeInfo ? (
                        `${velodromeInfo.distance}m`
                      ) : (
                        <Skeleton />
                      )}
                    </TableData>
                  </TableRow>
                  <TableRow>
                    <BankTableHeader>センター部路面傾斜</BankTableHeader>
                    <TableData>
                      {velodromeInfo ? velodromeInfo.centerSlope : <Skeleton />}
                    </TableData>
                  </TableRow>
                  <TableRow>
                    <BankTableHeader>直線部路面傾斜</BankTableHeader>
                    <TableData>
                      {velodromeInfo ? (
                        velodromeInfo.straightSlope
                      ) : (
                        <Skeleton />
                      )}
                    </TableData>
                  </TableRow>
                </tbody>
              </BankTable>
              <BankTable>
                <tbody>
                  <TableRow>
                    <BankTableHeader>ホーム幅員</BankTableHeader>
                    <TableData>
                      {velodromeInfo ? (
                        `${velodromeInfo.homeWidth}m`
                      ) : (
                        <Skeleton />
                      )}
                    </TableData>
                  </TableRow>
                  <TableRow>
                    <BankTableHeader>バック幅員</BankTableHeader>
                    <TableData>
                      {velodromeInfo ? (
                        `${velodromeInfo.backWidth}m`
                      ) : (
                        <Skeleton />
                      )}
                    </TableData>
                  </TableRow>
                  <TableRow>
                    <BankTableHeader>センター幅員</BankTableHeader>
                    <TableData>
                      {velodromeInfo ? (
                        `${velodromeInfo.centerWidth}m`
                      ) : (
                        <Skeleton />
                      )}
                    </TableData>
                  </TableRow>
                </tbody>
              </BankTable>
            </TableWrapper>
          </BankDetail>
        </BankInfo>
      </Section>
      <Section>
        <Headline2>所在地・連絡先</Headline2>
        <AccessFields>
          <AccessField icon="map">
            {velodromeInfo ? (
              velodromeInfo.address
            ) : (
              <Skeleton style={{ maxWidth: '300px' }} />
            )}
          </AccessField>
          <div>
            <iframe
              src={velodromeInfo?.mapSrc || ''}
              width="100%"
              height="160px"
              allowFullScreen={false}
              loading="lazy"
            />
          </div>
          <AccessField icon="tel">
            {velodromeInfo ? (
              velodromeInfo.phoneNumber
            ) : (
              <Skeleton style={{ maxWidth: '300px' }} />
            )}
          </AccessField>
          <AccessField icon="web">
            <AnchorLink
              href={velodromeInfo?.siteUrl || ''}
              target="_blank"
              rel="noreferrer"
            >
              {velodromeInfo ? (
                velodromeInfo.siteUrl
              ) : (
                <Skeleton style={{ maxWidth: '300px' }} />
              )}
            </AnchorLink>
          </AccessField>
        </AccessFields>
      </Section>
      {velodromeInfo?.access && (
        <Section>
          <Headline2>アクセス</Headline2>
          <Table>
            <tbody>
              {velodromeInfo.access.map(ac => (
                <TableRow key={ac.header}>
                  <AccessTableHeader>
                    <Text>{ac.header}</Text>
                  </AccessTableHeader>
                  <TableData>
                    <Text>{ac.body}</Text>
                  </TableData>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </Section>
      )}
      {velodromeInfo?.admissionFee && (
        <Section>
          <Headline2>入場料</Headline2>
          <Table>
            <tbody>
              {velodromeInfo.admissionFee.map(af => (
                <TableRow key={af.header}>
                  <FeeTableHeader>
                    <Text>{af.header}</Text>
                  </FeeTableHeader>
                  <TableData>
                    <Text>{af.body}</Text>
                  </TableData>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </Section>
      )}
      <Section>
        <Headline2>施設案内</Headline2>
        <Text>
          {velodromeInfo ? velodromeInfo.guide : <Skeleton count={3} />}
        </Text>
      </Section>
    </>
  );
};

export default VelodromeInfoTemplate;
